import { useState, useEffect, useRef } from 'react';
import API from './api';

const LOADING_MESSAGES = [
  "Consulting the official blame registry...",
  "Scanning job descriptions for suspects...",
  "Cross-referencing responsibilities...",
  "Identifying the responsible party...",
  "Checking who signed that clause...",
  "Navigating the org chart labyrinth...",
  "Throwing the net wide...",
  "The evidence is overwhelming...",
  "Almost caught the culprit...",
];

const PLACEHOLDER_QUESTIONS = [
  "Who is responsible for maintaining the ship's fire detection system?",
  "Whose job is it to ensure crew certificates are up to date?",
  "Who handles port state control inspections?",
  "Who is responsible when cargo documentation is missing?",
  "Whose responsibility is it to conduct emergency drills?",
];

const RADAR_STYLE = `
@keyframes radarSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes radarPing {
  0%   { transform: scale(1);   opacity: 1; }
  100% { transform: scale(2.4); opacity: 0; }
}
@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}
@keyframes scanLine {
  0%   { top: 0%; }
  100% { top: 100%; }
}
@keyframes glow {
  0%, 100% { box-shadow: 0 0 12px #e8001d55; }
  50%       { box-shadow: 0 0 28px #e8001daa, 0 0 48px #e8001d44; }
}
@keyframes typewriter {
  from { width: 0; }
  to   { width: 100%; }
}
@keyframes verdictPop {
  0%   { transform: scale(0.85); opacity: 0; }
  60%  { transform: scale(1.04); }
  100% { transform: scale(1);    opacity: 1; }
}
`;

function RadarAnimation() {
  return (
    <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 20px' }}>
      {/* Ping rings */}
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: '2px solid #e8001d44',
          animation: `radarPing 2s ease-out ${i * 0.65}s infinite`,
        }} />
      ))}
      {/* Static circles */}
      {[0.33, 0.66, 1].map(f => (
        <div key={f} style={{
          position: 'absolute',
          inset: `${(1 - f) * 50}%`,
          borderRadius: '50%',
          border: '1px solid #e8001d22',
        }} />
      ))}
      {/* Cross hairs */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', height: 1, background: '#e8001d22' }} />
      </div>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 1, height: '100%', background: '#e8001d22' }} />
      </div>
      {/* Rotating sweep */}
      <div style={{
        position: 'absolute', inset: 0,
        borderRadius: '50%', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', bottom: '50%', left: '50%',
          width: '50%', height: 1,
          background: 'linear-gradient(to right, transparent, #e8001dcc)',
          transformOrigin: 'left center',
          animation: 'radarSpin 2s linear infinite',
        }} />
      </div>
      {/* Center dot */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#e8001d',
          boxShadow: '0 0 10px #e8001d',
        }} />
      </div>
    </div>
  );
}

function LoadingMessageCycler() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      color: '#e8001d', fontFamily: 'monospace', fontSize: 13,
      textAlign: 'center', minHeight: 20,
      animation: 'blink 2s ease-in-out infinite',
    }}>
      {LOADING_MESSAGES[idx]}
    </div>
  );
}

function AnswerDisplay({ answer }) {
  const sections = parseAnswer(answer);
  return (
    <div style={{ animation: 'fadeSlideUp 0.5s ease forwards' }}>
      {sections.map((sec, i) => (
        <div key={i} style={{
          marginBottom: 18,
          animation: `fadeSlideUp 0.4s ease ${i * 0.1}s both`,
        }}>
          {sec.type === 'verdict' ? (
            <div style={{
              background: 'linear-gradient(135deg, rgba(232,0,29,0.15), rgba(232,0,29,0.05))',
              border: '1px solid rgba(232,0,29,0.5)',
              borderRadius: 10,
              padding: '16px 20px',
              animation: 'verdictPop 0.5s ease forwards',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 3,
                color: '#e8001d', marginBottom: 8, textTransform: 'uppercase',
              }}>
                ⚖️ OFFICIAL VERDICT
              </div>
              <div style={{ color: '#ffffff', fontSize: 15, fontWeight: 600, lineHeight: 1.6 }}>
                {sec.content}
              </div>
            </div>
          ) : (
            <div>
              {sec.heading && (
                <div style={{
                  fontSize: 13, fontWeight: 700, color: '#94a3b8',
                  marginBottom: 8, letterSpacing: 0.5,
                }}>
                  {sec.heading}
                </div>
              )}
              <div style={{
                color: '#cbd5e1', fontSize: 14, lineHeight: 1.75,
                whiteSpace: 'pre-wrap',
              }}>
                {sec.content}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function parseAnswer(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const sections = [];
  let current = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (current) current.content += '\n';
      continue;
    }
    // Detect verdict line
    if (trimmed.startsWith('⚖️') || trimmed.toLowerCase().startsWith('verdict')) {
      if (current) sections.push(current);
      const verdictContent = trimmed.replace(/^⚖️\s*\*?\*?VERDICT:?\*?\*?\s*/i, '').trim();
      current = { type: 'verdict', heading: null, content: verdictContent };
      continue;
    }
    // Detect section heading (emoji + bold markdown)
    const headingMatch = trimmed.match(/^(🎯|📋|🔍|⚓|🚨|📌|✅|⚠️)\s*\*?\*?(.+?)\*?\*?:?\s*$/);
    if (headingMatch) {
      if (current) sections.push(current);
      current = { type: 'section', heading: `${headingMatch[1]} ${headingMatch[2]}`, content: '' };
      continue;
    }
    if (current) {
      current.content += (current.content ? '\n' : '') + trimmed;
    } else {
      current = { type: 'section', heading: null, content: trimmed };
    }
  }
  if (current) sections.push(current);
  return sections.filter(s => s.content.trim());
}

export default function BlameCatcher({ setPage, onLogout }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [placeholder, setPlaceholder] = useState(0);
  const [history, setHistory] = useState([]);
  const [showGuide, setShowGuide] = useState(false);
  const [docStatus, setDocStatus] = useState(null);     // { indexed_chunks, indexed_files, doc_available }
  const [lastMeta, setLastMeta] = useState(null);       // { used_rag, chunks_retrieved, sources_used }
  const [selfTestLoading, setSelfTestLoading] = useState(false);
  const [selfTestResult, setSelfTestResult] = useState(null);
  const [showSelfTest, setShowSelfTest] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setPlaceholder(i => (i + 1) % PLACEHOLDER_QUESTIONS.length), 4000);
    return () => clearInterval(t);
  }, []);

  // Fetch document index status on mount
  useEffect(() => {
    API.get('/blamecatcher/status')
      .then(r => setDocStatus(r.data))
      .catch(() => {});
  }, []);

  const handleAsk = async () => {
    if (!question.trim() || loading) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await API.post('/blamecatcher/ask', { question: question.trim() });
      const data = res.data;
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data.answer);
        setLastMeta({
          used_rag: data.used_rag ?? false,
          chunks_retrieved: data.chunks_retrieved ?? 0,
          sources_used: data.sources_used ?? [],
        });
        setHistory(h => [{ question: question.trim(), answer: data.answer }, ...h].slice(0, 5));
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAsk();
  };

  const loadHistoryItem = (item) => {
    setQuestion(item.question);
    setResult(item.answer);
    setError('');
    setLastMeta(null);
  };

  const handleSelfTest = async () => {
    setSelfTestLoading(true);
    setSelfTestResult(null);
    try {
      const res = await API.post('/blamecatcher/self-test');
      setSelfTestResult(res.data);
    } catch (e) {
      setSelfTestResult({ error: e.response?.data?.detail || 'Connection error' });
    } finally {
      setSelfTestLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <style>{RADAR_STYLE}</style>

      {/* Header */}
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={() => setPage('dashboard')}>← BACK</button>
        <div style={styles.headerCenter}>
          <div style={styles.headerBadge}>INTERNAL INVESTIGATION DIVISION</div>
          <div style={styles.headerTitle}>BlameCatcher™</div>
          <div style={styles.headerSub}>Whose Job Is This Anyway?</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button style={styles.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
          <button style={styles.logoutBtn} onClick={onLogout}>LOGOUT</button>
        </div>
      </header>

      {showGuide && (
        <div style={styles.overlay} onClick={() => setShowGuide(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>BlameCatcher™ — How to Guide</div>
                <div style={styles.modalSub}>AI Responsibility Investigator</div>
              </div>
              <button style={styles.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.gSection}>
                <div style={styles.gTitle}>What is BlameCatcher?</div>
                <div style={styles.gText}>BlameCatcher is an AI agent that reads your official <strong style={styles.hl}>Job Description — Roles and Responsibilities</strong> document (IMS General #14) and answers any question about who is responsible for what. It cites the exact clause from your document and delivers a clear verdict.</div>
              </div>
              <div style={styles.gSection}>
                <div style={styles.gTitle}>Before You Start</div>
                <div style={styles.noteList}>
                  {['Upload your Job Description document to QDMS → GENERAL → slot #14.',
                    'The document can be PDF, Word (.docx), or Excel format.',
                    'BlameCatcher reads the entire document — the more complete it is, the better the answers.',
                  ].map((n,i) => <div key={i} style={styles.noteItem}><span style={styles.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
              <div style={styles.gSection}>
                <div style={styles.gTitle}>Step-by-Step</div>
                {[
                  { n:'1', t:'Type your question', d:'Ask about any task, document, system, or process. Be specific for best results. Example: "Who is responsible for maintaining the Ship Security Plan?"' },
                  { n:'2', t:'Press Enter or click Investigate', d:'BlameCatcher sends your question along with the entire Job Description to Claude AI.' },
                  { n:'3', t:'Read the structured verdict', d:'The answer is broken into: WHO\'S ON THE HOOK, WHAT THEY MUST DO, FINE PRINT, and a final VERDICT sentence.' },
                  { n:'4', t:'Use Case History', d:'Your last 5 investigations are saved in the sidebar. Click any to reload the answer.' },
                ].map(step => (
                  <div key={step.n} style={styles.step}>
                    <div style={styles.stepN}>{step.n}</div>
                    <div><div style={styles.stepT}>{step.t}</div><div style={styles.gText}>{step.d}</div></div>
                  </div>
                ))}
              </div>
              <div style={styles.gSection}>
                <div style={styles.gTitle}>Tips for Better Answers</div>
                <div style={styles.noteList}>
                  {[
                    'Ask about specific documents, systems, or equipment ("who is responsible for the ORB entries?" not "who does ORB?")',
                    'Mention context if relevant ("during port stay" or "when the DPA is unavailable")',
                    'If the document does not cover the topic, BlameCatcher will say so and offer a best-estimate from maritime convention.',
                  ].map((n,i) => <div key={i} style={styles.noteItem}><span style={styles.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main style={styles.main}>
        <div style={styles.layout}>

          {/* Left panel — history */}
          {history.length > 0 && (
            <div style={styles.historyPanel}>
              <div style={styles.historyTitle}>📁 CASE FILES</div>
              {history.map((item, i) => (
                <button
                  key={i}
                  style={styles.historyItem}
                  onClick={() => loadHistoryItem(item)}
                >
                  <div style={styles.historyCaseNum}>CASE #{history.length - i}</div>
                  <div style={styles.historyQ}>{item.question.slice(0, 60)}{item.question.length > 60 ? '…' : ''}</div>
                </button>
              ))}
            </div>
          )}

          {/* Main content */}
          <div style={styles.centerPanel}>

            {/* Document Index Status */}
            <div style={styles.statusRow}>
              {docStatus ? (
                docStatus.doc_available ? (
                  <div style={styles.statusBadgeGreen}>
                    <span style={styles.statusDotGreen} />
                    <span>
                      <strong>{docStatus.indexed_chunks}</strong> chunks indexed
                      across <strong>{docStatus.indexed_files?.length ?? 0}</strong> file(s) —{' '}
                      <span style={{ opacity: 0.75 }}>Semantic search active</span>
                    </span>
                  </div>
                ) : (
                  <div style={styles.statusBadgeWarn}>
                    <span>⚠ No indexed content — upload General #14 via QDMS for accurate answers</span>
                    <button style={styles.statusLink} onClick={() => setPage('ims')}>Open QDMS →</button>
                  </div>
                )
              ) : (
                <div style={styles.statusBadgeBlue}>
                  <span style={styles.sourceDot} />
                  Powered by IMS General #14 — Job Description &amp; Roles and Responsibilities
                </div>
              )}
              <button style={styles.selfTestBtn} onClick={() => setShowSelfTest(true)}>
                ⚡ Run Self-Test
              </button>
            </div>

            {/* Self-Test Modal */}
            {showSelfTest && (
              <div style={styles.overlay} onClick={() => setShowSelfTest(false)}>
                <div style={styles.modal} onClick={e => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <div>
                      <div style={styles.modalTitle}>Agent Self-Test</div>
                      <div style={styles.modalSub}>VERIFY DOCUMENT RETRIEVAL IS WORKING</div>
                    </div>
                    <button style={styles.modalClose} onClick={() => setShowSelfTest(false)}>✕</button>
                  </div>
                  <div style={styles.modalBody}>
                    <div style={styles.gSection}>
                      <div style={styles.gTitle}>What This Tests</div>
                      <div style={styles.gText}>
                        Sends a fixed known-answer question to BlameCatcher and shows exactly which
                        document sections were retrieved. If <strong style={styles.hl}>used_rag: true</strong> and chunks are returned,
                        the agent is reading your documents correctly. If not, re-index via QDMS.
                      </div>
                    </div>
                    <div style={styles.gSection}>
                      <div style={styles.gTitle}>Test Question</div>
                      <div style={styles.selfTestQ}>
                        "Who is responsible for ISM Code implementation and maintaining the Safety Management System?"
                      </div>
                    </div>
                    {!selfTestResult && (
                      <button
                        style={{ ...styles.askBtn, opacity: selfTestLoading ? 0.5 : 1 }}
                        onClick={handleSelfTest}
                        disabled={selfTestLoading}
                      >
                        {selfTestLoading ? '⏳ Running test...' : '▶ Run Test Now'}
                      </button>
                    )}
                    {selfTestResult && (
                      <>
                        {/* Coverage result */}
                        <div style={selfTestResult.used_rag ? styles.selfTestPass : styles.selfTestFail}>
                          <div style={styles.selfTestStatusLine}>
                            <span style={{ fontSize: 18 }}>{selfTestResult.used_rag ? '✓' : '⚠'}</span>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13 }}>
                                {selfTestResult.used_rag
                                  ? `RAG Active — ${selfTestResult.chunks_retrieved} sections retrieved`
                                  : 'Fallback Mode — documents not indexed'}
                              </div>
                              {selfTestResult.sources_used?.length > 0 && (
                                <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>
                                  Sources: {selfTestResult.sources_used.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Answer preview */}
                        {selfTestResult.answer && (
                          <div style={styles.gSection}>
                            <div style={styles.gTitle}>Agent Response Preview</div>
                            <div style={{ ...styles.gText, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 14, lineHeight: 1.7 }}>
                              {selfTestResult.answer.slice(0, 600)}{selfTestResult.answer.length > 600 ? '…' : ''}
                            </div>
                          </div>
                        )}
                        {selfTestResult.error && (
                          <div style={styles.selfTestFail}>
                            <div style={styles.selfTestStatusLine}>
                              <span>✕</span>
                              <span>{selfTestResult.error}</span>
                            </div>
                          </div>
                        )}
                        <button style={styles.newCaseBtn} onClick={() => setSelfTestResult(null)}>
                          Run Again
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Question box */}
            <div style={styles.inputCard}>
              <div style={styles.inputLabel}>
                🔍 DESCRIBE THE SITUATION OR TASK
              </div>
              <textarea
                ref={textareaRef}
                style={styles.textarea}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={PLACEHOLDER_QUESTIONS[placeholder]}
                rows={4}
                disabled={loading}
              />
              <div style={styles.inputFooter}>
                <span style={styles.hint}>Ctrl+Enter to submit</span>
                <button
                  style={{
                    ...styles.askBtn,
                    opacity: (!question.trim() || loading) ? 0.5 : 1,
                    cursor: (!question.trim() || loading) ? 'not-allowed' : 'pointer',
                    animation: question.trim() && !loading ? 'glow 2s ease-in-out infinite' : 'none',
                  }}
                  onClick={handleAsk}
                  disabled={!question.trim() || loading}
                >
                  {loading ? '⏳ INVESTIGATING...' : '🎯 CATCH THE CULPRIT'}
                </button>
              </div>
            </div>

            {/* Loading state */}
            {loading && (
              <div style={styles.loadingCard}>
                <RadarAnimation />
                <LoadingMessageCycler />
                <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                  Scanning 400+ pages of responsibility documentation
                </div>
              </div>
            )}

            {/* Error state */}
            {!loading && error && (
              <div style={styles.errorCard}>
                <div style={styles.errorIcon}>⚠️</div>
                <div style={styles.errorTitle}>Investigation Stalled</div>
                <div style={styles.errorMsg}>{error}</div>
                {error.includes('QDMS') || error.includes('upload') ? (
                  <button style={styles.errorAction} onClick={() => setPage('ims')}>
                    → Open QDMS to Upload Document
                  </button>
                ) : null}
              </div>
            )}

            {/* Answer */}
            {!loading && result && !error && (
              <div style={styles.resultCard}>
                <div style={styles.resultHeader}>
                  <div style={styles.stampLabel}>CASE CLOSED</div>
                  <div style={styles.resultQ}>Re: "{question}"</div>
                </div>
                <div style={styles.divider} />
                <AnswerDisplay answer={result} />

                {/* Sources Panel */}
                {lastMeta && (
                  <div style={lastMeta.used_rag ? styles.sourcesPanel : styles.sourcesPanelWarn}>
                    <div style={styles.sourcesPanelRow}>
                      <span style={{ fontSize: 13 }}>{lastMeta.used_rag ? '✓' : '⚠'}</span>
                      <div style={styles.sourcesPanelText}>
                        {lastMeta.used_rag ? (
                          <>
                            <strong>{lastMeta.chunks_retrieved} document sections</strong> retrieved from{' '}
                            <strong>{lastMeta.sources_used.length}</strong> file(s) via semantic search
                          </>
                        ) : (
                          <>Fallback mode — index your documents in QDMS for full coverage</>
                        )}
                      </div>
                    </div>
                    {lastMeta.sources_used.length > 0 && (
                      <div style={styles.sourcesList}>
                        {lastMeta.sources_used.map((s, i) => (
                          <span key={i} style={styles.sourceChip}>{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={styles.resultFooter}>
                  <button
                    style={styles.newCaseBtn}
                    onClick={() => { setResult(null); setQuestion(''); setError(''); textareaRef.current?.focus(); }}
                  >
                    + OPEN NEW CASE
                  </button>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && !result && !error && (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🕵️</div>
                <div style={styles.emptyTitle}>BlameCatcher is on standby</div>
                <div style={styles.emptyDesc}>
                  Type a task or situation above. BlameCatcher will read the Job Description document
                  and tell you exactly who is responsible — diplomatically, of course.
                </div>
                <div style={styles.examplesTitle}>EXAMPLE QUESTIONS</div>
                <div style={styles.examples}>
                  {PLACEHOLDER_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      style={styles.exampleChip}
                      onClick={() => { setQuestion(q); textareaRef.current?.focus(); }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1527 50%, #0a0f1e 100%)',
    color: '#e2e8f0',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 28px',
    borderBottom: '1px solid rgba(232,0,29,0.2)',
    background: 'rgba(10,15,30,0.95)',
    backdropFilter: 'blur(10px)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  backBtn: {
    background: 'transparent', border: '1px solid #1e293b',
    color: '#94a3b8', padding: '8px 16px', borderRadius: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: 1,
    transition: 'all 0.2s',
  },
  headerCenter: {
    textAlign: 'center',
  },
  headerBadge: {
    fontSize: 9, letterSpacing: 3, color: '#e8001d',
    fontWeight: 700, marginBottom: 2, textTransform: 'uppercase',
  },
  headerTitle: {
    fontSize: 22, fontWeight: 800, letterSpacing: 1,
    background: 'linear-gradient(90deg, #ffffff, #e8001d)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  headerSub: {
    fontSize: 11, color: '#64748b', letterSpacing: 2, marginTop: 2,
  },
  logoutBtn: {
    background: 'transparent', border: '1px solid #1e293b',
    color: '#94a3b8', padding: '8px 16px', borderRadius: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: 1,
  },
  main: {
    maxWidth: 1100, margin: '0 auto', padding: '32px 24px',
  },
  layout: {
    display: 'flex', gap: 24, alignItems: 'flex-start',
  },
  historyPanel: {
    width: 220, flexShrink: 0,
    background: 'rgba(15,20,40,0.6)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, padding: 16,
  },
  historyTitle: {
    fontSize: 10, fontWeight: 700, letterSpacing: 2,
    color: '#475569', marginBottom: 12, textTransform: 'uppercase',
  },
  historyItem: {
    width: '100%', background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
    padding: '10px 12px', marginBottom: 8, cursor: 'pointer',
    textAlign: 'left', transition: 'all 0.2s',
  },
  historyCaseNum: {
    fontSize: 9, color: '#e8001d', fontWeight: 700,
    letterSpacing: 1, marginBottom: 4,
  },
  historyQ: {
    fontSize: 11, color: '#94a3b8', lineHeight: 1.4,
  },
  centerPanel: {
    flex: 1, minWidth: 0,
  },
  sourceBadge: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: 20, padding: '6px 14px', marginBottom: 20,
    fontSize: 11, color: '#60a5fa', width: 'fit-content',
  },
  sourceDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 6px #22c55e',
  },
  inputCard: {
    background: 'rgba(15,20,40,0.8)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14, padding: 24, marginBottom: 24,
  },
  inputLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: 2,
    color: '#64748b', marginBottom: 12, textTransform: 'uppercase',
  },
  textarea: {
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
    color: '#e2e8f0', fontSize: 14, lineHeight: 1.6,
    padding: '12px 16px', resize: 'vertical', outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 14,
  },
  hint: {
    fontSize: 11, color: '#334155',
  },
  askBtn: {
    background: 'linear-gradient(135deg, #e8001d, #b0001a)',
    border: 'none', borderRadius: 8, color: '#fff',
    padding: '12px 24px', fontSize: 13, fontWeight: 700,
    letterSpacing: 1, cursor: 'pointer', transition: 'all 0.2s',
    textTransform: 'uppercase',
  },
  loadingCard: {
    background: 'rgba(15,20,40,0.8)',
    border: '1px solid rgba(232,0,29,0.2)',
    borderRadius: 14, padding: '36px 24px',
    textAlign: 'center', marginBottom: 24,
  },
  errorCard: {
    background: 'rgba(239,68,68,0.07)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 14, padding: '28px 24px',
    textAlign: 'center', marginBottom: 24,
  },
  errorIcon: { fontSize: 32, marginBottom: 12 },
  errorTitle: { fontSize: 16, fontWeight: 700, color: '#f87171', marginBottom: 8 },
  errorMsg: { fontSize: 13, color: '#94a3b8', lineHeight: 1.6, marginBottom: 16 },
  errorAction: {
    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa', padding: '10px 20px', borderRadius: 8,
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  resultCard: {
    background: 'rgba(15,20,40,0.8)',
    border: '1px solid rgba(232,0,29,0.25)',
    borderRadius: 14, padding: 28, marginBottom: 24,
    animation: 'fadeSlideUp 0.4s ease forwards',
  },
  resultHeader: {
    display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20,
    flexWrap: 'wrap',
  },
  stampLabel: {
    background: 'rgba(232,0,29,0.15)', border: '2px solid rgba(232,0,29,0.5)',
    color: '#e8001d', padding: '4px 12px', borderRadius: 4,
    fontSize: 10, fontWeight: 800, letterSpacing: 2,
    flexShrink: 0, alignSelf: 'center',
  },
  resultQ: {
    fontSize: 13, color: '#64748b', fontStyle: 'italic', lineHeight: 1.5,
  },
  divider: {
    height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 24,
  },
  resultFooter: {
    marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.05)',
    display: 'flex', justifyContent: 'flex-end',
  },
  newCaseBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
    color: '#64748b', padding: '8px 18px', borderRadius: 8,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: 1,
    transition: 'all 0.2s',
  },
  emptyState: {
    textAlign: 'center', padding: '48px 24px',
  },
  emptyIcon: {
    fontSize: 64, marginBottom: 20, filter: 'drop-shadow(0 0 20px rgba(232,0,29,0.3))',
  },
  emptyTitle: {
    fontSize: 20, fontWeight: 700, color: '#e2e8f0', marginBottom: 12,
  },
  emptyDesc: {
    fontSize: 14, color: '#64748b', lineHeight: 1.7,
    maxWidth: 500, margin: '0 auto 32px',
  },
  examplesTitle: {
    fontSize: 10, letterSpacing: 3, color: '#334155',
    fontWeight: 700, marginBottom: 16, textTransform: 'uppercase',
  },
  examples: {
    display: 'flex', flexDirection: 'column', gap: 8,
    maxWidth: 540, margin: '0 auto',
  },
  exampleChip: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    color: '#94a3b8', padding: '10px 16px', borderRadius: 8,
    cursor: 'pointer', fontSize: 12, textAlign: 'left',
    transition: 'all 0.2s', lineHeight: 1.4,
  },
  // Document status row
  statusRow: { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:20, flexWrap:'wrap' },
  statusBadgeGreen: { display:'flex', alignItems:'center', gap:8, background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:20, padding:'6px 14px', fontSize:11, color:'#86efac', flex:1 },
  statusBadgeWarn: { display:'flex', alignItems:'center', gap:10, background:'rgba(234,179,8,0.07)', border:'1px solid rgba(234,179,8,0.25)', borderRadius:20, padding:'6px 14px', fontSize:11, color:'#fde047', flex:1, flexWrap:'wrap' },
  statusBadgeBlue: { display:'flex', alignItems:'center', gap:8, background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:20, padding:'6px 14px', fontSize:11, color:'#60a5fa', flex:1 },
  statusDotGreen: { width:6, height:6, borderRadius:'50%', background:'#22c55e', boxShadow:'0 0 6px #22c55e', flexShrink:0 },
  statusLink: { background:'transparent', border:'1px solid rgba(234,179,8,0.3)', color:'#fde047', padding:'3px 10px', borderRadius:6, fontSize:10, cursor:'pointer', whiteSpace:'nowrap' },
  selfTestBtn: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', color:'#94a3b8', padding:'6px 14px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 },
  // Sources panel (below answer)
  sourcesPanel: { background:'rgba(34,197,94,0.05)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:10, padding:'12px 16px', marginTop:16, marginBottom:4 },
  sourcesPanelWarn: { background:'rgba(234,179,8,0.05)', border:'1px solid rgba(234,179,8,0.2)', borderRadius:10, padding:'12px 16px', marginTop:16, marginBottom:4 },
  sourcesPanelRow: { display:'flex', alignItems:'flex-start', gap:10, marginBottom:8 },
  sourcesPanelText: { fontSize:12, color:'#94a3b8', lineHeight:1.5 },
  sourcesList: { display:'flex', flexWrap:'wrap', gap:6 },
  sourceChip: { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#64748b', padding:'3px 10px', borderRadius:6, fontSize:10, fontFamily:'monospace' },
  // Self-test results
  selfTestQ: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#94a3b8', fontStyle:'italic', lineHeight:1.6 },
  selfTestPass: { background:'rgba(34,197,94,0.07)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:10, padding:'14px 16px', color:'#86efac' },
  selfTestFail: { background:'rgba(234,179,8,0.07)', border:'1px solid rgba(234,179,8,0.25)', borderRadius:10, padding:'14px 16px', color:'#fde047' },
  selfTestStatusLine: { display:'flex', alignItems:'flex-start', gap:10, fontSize:12, lineHeight:1.5 },
  guideBtn: { background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#64748b', padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'monospace', whiteSpace:'nowrap' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  modal: { background:'#111d2e', border:'1px solid #1e2d40', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'82vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.7)' },
  modalHeader: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'22px 26px 18px', borderBottom:'1px solid #1e2d40', flexShrink:0 },
  modalTitle: { fontSize:15, fontWeight:700, color:'#e8edf4', marginBottom:4 },
  modalSub: { fontSize:11, fontFamily:'monospace', color:'#c9a84c', letterSpacing:1 },
  modalClose: { background:'transparent', border:'none', color:'#64748b', fontSize:16, cursor:'pointer', padding:'0 0 0 16px', lineHeight:1 },
  modalBody: { overflowY:'auto', padding:'22px 26px', display:'flex', flexDirection:'column', gap:22 },
  gSection: {},
  gTitle: { fontFamily:'monospace', fontSize:10, fontWeight:700, letterSpacing:2, color:'#c9a84c', marginBottom:10, textTransform:'uppercase' },
  gText: { fontSize:12, color:'#8a9ab0', lineHeight:1.7 },
  hl: { color:'#e8edf4' },
  step: { display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 },
  stepN: { flexShrink:0, width:26, height:26, borderRadius:'50%', background:'rgba(232,0,29,0.12)', border:'1px solid rgba(232,0,29,0.3)', color:'#e8001d', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'monospace' },
  stepT: { fontSize:13, fontWeight:600, color:'#e8edf4', marginBottom:3 },
  noteList: { display:'flex', flexDirection:'column', gap:7 },
  noteItem: { display:'flex', gap:8, fontSize:12, color:'#8a9ab0', lineHeight:1.6 },
  noteDot: { color:'#c9a84c', flexShrink:0 },
};
